import React, { useEffect, useState } from "react";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import _ from "lodash";
import PageListHeader from "../../components/pageListHeader";
import { TextInput, Text, Stack, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTabNavigate } from "../../hooks/useTabNavigate";

const defaultIr56mValues = {
  section: "6A1",
  employerReferenceNo: "",
  payerName: "",
  signerName: "",
  designation: "",
  defaultCapacity: "CONSULTANT",
  version: "M0001",
};

const Ir56mSetting = () => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const tabNav = useTabNavigate();
  const [settingId, setSettingId] = useState(null);
  const [ir56m, setIr56m] = useState(defaultIr56mValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const setField = (field, value) => {
    setIr56m((prev) => ({ ...prev, [field]: value }));
  };

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });

      if (!result || _.isEmpty(result.docs)) {
        setSettingId(null);
        setIr56m({ ...defaultIr56mValues });
        return;
      }

      const setting = result.docs[0];
      setSettingId(setting._id);
      setIr56m({
        ...defaultIr56mValues,
        ...(setting.ir56m || {}),
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      let result;

      if (settingId) {
        const existing = await api.getById({
          apiEntity: "setting",
          id: settingId,
        });
        result = await api.updateById({
          apiEntity: "setting",
          id: settingId,
          values: {
            ...existing,
            ir56m,
          },
        });
      } else {
        result = await api.add({
          apiEntity: "setting",
          values: { ir56m },
        });
        if (result?.success && result?.data?._id) {
          setSettingId(result.data._id);
        }
      }

      if (result?.success) {
        showNotification({
          title: t("Save"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message: t("settings.saved"),
        });
        return;
      }

      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: api.getErrorMsg(result?.error) || t("settings.saveFailed"),
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("settings.saveFailed"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("settings.ir56m.title")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: loading,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />

      {!loading && (
        <Stack spacing="md">
          <FormSectionCard>
            <Title order={5}>{t("settings.ir56m.employerReturn")}</Title>
            <Text size="xs" color="dimmed" mb="md">
              {t("settings.ir56m.description")}
            </Text>
            <TextInput
              label={t("settings.ir56m.payerName")}
              value={ir56m.payerName}
              onChange={(e) => setField("payerName", e.currentTarget.value)}
              required
              mb="md"
            />
          </FormSectionCard>

          <FormSectionCard>
            <Title order={5}>{t("settings.ir56m.employerFileNo")}</Title>
            <TextInput
              label={t("settings.ir56m.section")}
              value={ir56m.section}
              onChange={(e) => setField("section", e.currentTarget.value)}
              placeholder="6A1"
              maxLength={3}
              required
              mb="md"
            />
            <TextInput
              label={t("settings.ir56m.ern")}
              value={ir56m.employerReferenceNo}
              onChange={(e) =>
                setField("employerReferenceNo", e.currentTarget.value)
              }
              placeholder="01234561"
              maxLength={8}
              required
            />
          </FormSectionCard>

          <FormSectionCard>
            <Title order={5}>{t("settings.ir56m.authorizedSigner")}</Title>
            <TextInput
              label={t("settings.ir56m.signerName")}
              value={ir56m.signerName}
              onChange={(e) => setField("signerName", e.currentTarget.value)}
              placeholder="LEE, MAN MAN"
              required
              mb="md"
            />
            <TextInput
              label={t("settings.ir56m.designation")}
              value={ir56m.designation}
              onChange={(e) => setField("designation", e.currentTarget.value)}
              placeholder="DIRECTOR"
              required
            />
          </FormSectionCard>

          <FormSectionCard>
            <Title order={5}>{t("settings.ir56m.defaults")}</Title>
            <TextInput
              label={t("settings.ir56m.defaultCapacity")}
              value={ir56m.defaultCapacity}
              onChange={(e) =>
                setField("defaultCapacity", e.currentTarget.value)
              }
              placeholder="CONSULTANT"
              mb="md"
            />
            <TextInput
              label={t("settings.ir56m.version")}
              value={ir56m.version}
              disabled
            />
          </FormSectionCard>
        </Stack>
      )}
    </Page>
  );
};

export default Ir56mSetting;
